import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_LOST_FOUND_ITEMS } from '../graphql/lost-found-queries';
import LostFoundItem from '../components/LostFoundItem';
import Spinner from '../components/Spinner';
import { LOST_FOUND_CATEGORY } from '../components/LostFoundForm';

const ITEMS_PER_PAGE = 10;

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: LOST_FOUND_CATEGORY.LOST_ITEM_FOUND, label: 'Lost Item Found' },
  { value: LOST_FOUND_CATEGORY.REPORTED_LOST_ITEM, label: 'Reported Lost Item' },
];

export default function LostAndFound() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const loader = useRef(null);

  const { loading, data, error, fetchMore } = useQuery(GET_LOST_FOUND_ITEMS, {
    variables: {
      page: 1,
      limit: ITEMS_PER_PAGE,
      search: search || undefined,
      category: categoryFilter || undefined,
      isArchived: false,
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data?.lostFoundItems) {
      setItems(data.lostFoundItems.items);
      setHasMore(data.lostFoundItems.hasMore);
      setPage(1);
    }
  }, [data]);

  const loadMoreItems = useCallback(() => {
    const nextPage = page + 1;
    fetchMore({
      variables: {
        page: nextPage,
        limit: ITEMS_PER_PAGE,
        search: search || undefined,
        category: categoryFilter || undefined,
        isArchived: false,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          lostFoundItems: {
            ...fetchMoreResult.lostFoundItems,
            items: [
              ...prev.lostFoundItems.items,
              ...fetchMoreResult.lostFoundItems.items,
            ],
          },
        };
      },
    }).then(() => {
      setPage(nextPage);
      setHasMore(data?.lostFoundItems?.hasMore || false);
    }).catch(error => {
      console.error('Error loading more items:', error);
    });
  }, [page, fetchMore, search, categoryFilter, data]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 1.0,
    };

    const handleObserverCallback = (entities) => {
      const target = entities[0];
      if (target.isIntersecting && hasMore && !loading) {
        loadMoreItems();
      }
    };

    // Observer for infinite scrolling
    const observer = new IntersectionObserver(handleObserverCallback, options);
    const currentLoader = loader.current;
    
    if (currentLoader) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, loadMoreItems]); // Add dependencies to prevent stale closures

  const handleStatusChange = (id, newStatus) => {
    // Keep open/found items active; archived states leave this view.
    if (!['PENDING', 'FOUND'].includes(newStatus)) {
      setItems(prevItems => prevItems.filter(item => item.id !== id));
    } else {
      setItems(prevItems => prevItems.map(item => (
        item.id === id ? { ...item, status: newStatus } : item
      )));
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // The query will be re-run with the new search term
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-white lg:text-gray-900">Lost and Found</h1>
        <div className="flex space-x-3">
          <Link
            to="/dashboard/lost-found-archive"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white text-gray-700 hover:bg-gray-50"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            View Archive
          </Link>
          <Link
            to="/dashboard/lost-found/create"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Lost Item
          </Link>
        </div>
      </div>

      {/* Search and filter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
          {/* Search group: input + button together */}
          <div className="flex flex-1 min-w-0 gap-2">
            <div className="relative flex-1 min-w-0">
              <input
                type="text"
                className="block w-full h-10 rounded-lg border border-gray-300 bg-gray-50 py-2 pl-10 pr-4 text-gray-900 placeholder-gray-500 transition-colors focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Search by property or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search lost and found items"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center h-10 px-4 rounded-lg border border-transparent bg-blue-600 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex-shrink-0"
            >
              Search
            </button>
          </div>

          {/* Category filter */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <label htmlFor="category-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Category
            </label>
            <select
              id="category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="h-10 min-w-[180px] rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 pr-8 text-sm text-gray-900 transition-colors focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              aria-label="Filter by category"
            >
              {CATEGORY_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </form>
      </div>

      {/* Lost and Found Items List */}
      <div className="space-y-4">
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-sm font-medium text-red-800">Unable to load lost and found items</h3>
            <p className="mt-2 text-sm text-red-700">{error.message}</p>
          </div>
        ) : loading && page === 1 ? (
          <div className="flex justify-center p-12">
            <Spinner />
          </div>
        ) : items.length > 0 ? (
          <>
            {items.map((item) => (
              <LostFoundItem 
                key={item.id} 
                item={item} 
                onStatusChange={handleStatusChange}
              />
            ))}
            {loading && (
              <div className="flex justify-center p-4">
                <Spinner />
              </div>
            )}
            <div ref={loader} className="h-10" />
          </>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No active lost and found items</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new lost and found item.</p>
            <div className="mt-6">
              <Link
                to="/dashboard/lost-found/create"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Lost Item
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 
